import dayjs from 'dayjs';
import axios from 'axios';
import { Logger } from 'types';
import { SkuDetails } from '../analytics/types';

// Add to this constant to set stores that are microSites of DSG. This is used to pass the channel param to the product API
export const micrositeStores = ['g3'];

export function getCookie(cname: string) {
  const name = `${cname}=`;
  if (typeof document !== 'undefined') {
    const cookies = document.cookie.split(';');

    const foundCookie = cookies.find((cookie) => cookie.trimStart().startsWith(name));
    return foundCookie ? foundCookie.trimStart().substring(name.length) : '';
  }
  return '';
}

export const calculateAddToCartPrice = (skuPrice: number, cartCount: number) => {
  return (skuPrice * cartCount).toFixed(2);
};

export const checkIfPreOrderSKU = (sku: SkuDetails, log?: Logger['warn']) => {
  return sku?.descriptiveAttributes?.find((attribute) => {
    if (attribute.name === 'PRESALE_END_DATE') {
      try {
        const preOrderEndDate = dayjs(attribute.value, 'MM-DD-YYYY');
        const nextDay = dayjs().add(1, 'day');

        // If pre sale end date is in the future
        if (nextDay.diff(preOrderEndDate) < 0) {
          return true;
        }
        return false;
      } catch (error) {
        if (log) {
          log(error);
        }
        return false;
      }
    }

    return false;
  });
};

export const checkComingSoonEndDate = (comingSoonEndDate: string) => {
  const now = dayjs();
  const comingSoon = dayjs(comingSoonEndDate, 'MM-DD-YYYY');
  if (now.diff(comingSoon) < 0) {
    return true;
  }
  return false;
};

export const shouldDisplayAlertBanner = (
  isProductOOS: boolean,
  template: string,
  isSkuSelectionUndefined?: boolean,
) => {
  let showAlertBanner = isSkuSelectionUndefined ?? (template === 'edit' && isProductOOS);
  if (template === 'saveSelection' && isProductOOS) {
    showAlertBanner = true;
  }

  return showAlertBanner;
};

/**
 * Type for the parameters of the makeApiRequest function.
 */
type ApiRequestParams = {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: Record<string, unknown>;
  getUserToken: () => Promise<string | null>;
  onClose?: () => void;
  onSuccess?: () => void;
  callback?: (result: { state: 'SUCCESS' | 'FAILURE'; sku?: string }) => void;
  analyticsEventCallback?: (data: Record<string, unknown>) => void;
  eventData?: Record<string, unknown>;
};

/**
 * Makes an API request to the specified endpoint.
 * @param endpoint - The endpoint to make the request to.
 * @param method - The HTTP method to use.
 * @param data - The data to send with the request.
 * @param getUserToken - A function that returns a promise that resolves to a string or null.
 * @param onClose - A function to call when the request is complete.
 * @param onSuccess - A function to call when the request is successful.
 * @param callback - Optional callback passed through the window.quickview.openProductQuickview function
 * @param analyticsEventCallback - Optional callback for analytics events
 * @param eventData - The data to send with the event.
 * @returns A promise that resolves when the API request is complete.
 */
export const makeApiRequest = async ({
  endpoint,
  method,
  data,
  getUserToken,
  onClose,
  onSuccess,
  callback,
  analyticsEventCallback,
  eventData,
}: ApiRequestParams) => {
  const host = window.location ? window.location.host : '';
  const token = await getUserToken();
  let hasToken = false;
  if (typeof token === 'string') {
    hasToken = true;
  }

  try {
    const response = await axios(endpoint, {
      method,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        Cookie: host.includes('prod-') || host.includes('localhost') ? 'np0=true' : '',
        ...(hasToken ? { Authorization: token } : {}),
      },
      data,
    });

    if (!callback) {
      onClose?.();
      return;
    }

    if (response.status === 200) {
      onSuccess?.();
      callback({ state: 'SUCCESS', sku: typeof data?.sku === 'string' ? data.sku : undefined });
      if (analyticsEventCallback && eventData) {
        analyticsEventCallback(eventData);
      }
      onClose?.();
      return;
    }

    callback({ state: 'FAILURE' });
  } catch (error) {
    if (callback) {
      callback({ state: 'FAILURE' });
    }
  }
};
